import { useState, useEffect, useRef } from 'react';
import { Typography, Box, Link } from '@mui/material';
import { Popup } from '../../components/popup/popup';
import { CustomerClient } from '../../helpers/client';
import PhoneConfirmIcon from '../../logo/phoneconfirmation.png';
import { InputStyled, PhoneLogoImg } from './styles';
import { useTranslation } from 'react-i18next';
import { useRouteNavigator } from '../../routes/useRouteNavigator';
import { useReloginFlow } from '../../hooks/useReloginFlow';
import { ReloginConfirmation } from '../../components/auth/login/reloginConfirmation';
import { clearPhoneConfirmationRequired } from '@/helpers/sessionStorageService';

const ConfirmPhone = ({ isOpened }: { isOpened: boolean }) => {
  const routeNavigator = useRouteNavigator();
  const [isBusy, setIsBusy] = useState(false);
  const [code, setCode] = useState('');
  const [errorText, setErrorText] = useState<string>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval>>(null);
  const customersClient: CustomerClient = new CustomerClient();
  const { t } = useTranslation();
  const { showReloginDialog, reloginMessage, triggerRelogin, handleReloginConfirm } = useReloginFlow();

  useEffect(() => {
    return () => {
      if (cooldownRef.current) {
        clearInterval(cooldownRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpened) {
      clearPhoneConfirmationRequired();
    }
  }, [isOpened]);

  const startCooldown = () => {
    setResendCooldown(30);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendCode = async () => {
    setErrorText(null);
    setCode('');
    try {
      setIsBusy(true);
      await customersClient.sendPhoneConfirmation();
      startCooldown();
    } catch {
      console.error('Failed to send confirmation sms');
      setErrorText(t('account:sendPhoneConfirmationError'));
    } finally {
      setIsBusy(false);
    }
  };

  const confirmCode = async () => {
    setErrorText(null);
    try {
      setIsBusy(true);

      await customersClient.confirmPhone(code);
      triggerRelogin(t('account:phoneConfirmedSuccessfully'));
    } catch {
      console.error('Failed to confirm phone');
      setErrorText(t('account:confirmPhoneError'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <>
      <Popup
        submitOnEnter
        open={isOpened && !showReloginDialog}
        onClose={() => routeNavigator.goHome()}
        dialogTitle={t('account:confirmDialogTitle')}
        isFailure={!!errorText}
        failureText={errorText}
        dialogContent={
          <>
            <Box sx={{ display: 'flex' }}>
              <Box>
                <Typography sx={{ fontSize: '20px' }}>{t('account:confirmPhoneToContinue')}</Typography>
                <InputStyled
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={t('account:phoneConfirmationExample')}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mt: '8px', gap: '2px' }}>
                  <Typography sx={{ fontSize: '15px' }}>{t('account:didNotReceiveCode')}</Typography>
                  <Link
                    component="button"
                    type="button"
                    onClick={sendCode}
                    sx={{
                      fontSize: '15px',
                      opacity: isBusy || resendCooldown > 0 ? 0.5 : 1,
                      pointerEvents: isBusy || resendCooldown > 0 ? 'none' : 'auto',
                    }}
                  >
                    {resendCooldown > 0
                      ? `${t('account:resendConfirmationCode')} (${resendCooldown}s)`
                      : t('account:resendConfirmationCode')}
                  </Link>
                </Box>
              </Box>
              <PhoneLogoImg src={PhoneConfirmIcon} alt="Phone logo" />
            </Box>
          </>
        }
        onAction={confirmCode}
        isButtonDisabled={isBusy || code.length < 1}
        buttonContent={t('account:confirmPhoneOnPopup')}
      />
      <ReloginConfirmation open={showReloginDialog} message={reloginMessage} onConfirm={handleReloginConfirm} />
    </>
  );
};

export { ConfirmPhone };
